import { RefObject, useEffect, useRef } from 'react';

interface UseCloseOnClickOutsideParams {
    active: boolean;
    enabled: boolean;
    blockClickThrough: boolean;
    panelRef: RefObject<HTMLElement>;
    avoid?: (target: HTMLElement | null) => boolean;
    onCloseRequest: VoidFunction;
}

export const useCloseOnClickOutside = ({
    active,
    enabled,
    blockClickThrough,
    panelRef,
    avoid,
    onCloseRequest,
}: UseCloseOnClickOutsideParams) => {
    const latestRef = useRef({ enabled, blockClickThrough, avoid, onCloseRequest });
    latestRef.current = { enabled, blockClickThrough, avoid, onCloseRequest };

    useEffect(() => {
        if (!active) {
            return undefined;
        }
        // Backdrop is non-interactive (pointer-events: none), so detect outside clicks ourselves.
        const handleClickOutside = (event: MouseEvent) => {
            const {
                enabled: isEnabled,
                blockClickThrough: shouldBlockClickThrough,
                avoid: shouldAvoid,
                onCloseRequest: close,
            } = latestRef.current;
            if (!isEnabled) {
                return;
            }
            const target = event.target as HTMLElement | null;
            // OWC-OVERRIDE (DOM read, not a style override): owc-modal-dialog signals its open
            // state only through this class in v7.6.0 — no public API, event, or attribute — so
            // a click inside a modal cannot otherwise be told apart from a click outside the
            // sidesheet. Revisit on the next @one upgrade.
            const isModalOpen = document.querySelector('.owc-modal-dialog--visible') !== null;
            if (panelRef.current && !panelRef.current.contains(target) && !isModalOpen && !shouldAvoid?.(target)) {
                if (shouldBlockClickThrough) {
                    event.stopPropagation();
                    event.preventDefault();
                }
                close();
            }
        };
        document.addEventListener('click', handleClickOutside, true);
        return () => document.removeEventListener('click', handleClickOutside, true);
    }, [active, panelRef]);
};
